import {
  DocumentDuplicateIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/ui/button';
import { AllIntegrations } from '@/hooks/useIntegration';
import { SettingContentCard } from '@/sections/settings/components/primitives/SettingContentCard';
import { Account, Integration } from '@/types';
import { cn } from '@/utils/classname';

export interface InboundWebhookSectionProps {
  /** The hook account if it exists */
  hookAccount: Account | undefined;
  /** Generated webhook URL for display */
  hookUrl: string;
  /** Handler for generating a new webhook URL (hidden when undefined) */
  onGenerate?: () => void;
  /** Handler for disconnecting the webhook (hidden when undefined) */
  onDisconnect?: () => void;
  /** Handler for copying URL to clipboard */
  onCopy: () => void;
  /** Whether URL was recently copied (shows green flash) */
  isCopied?: boolean;
  /** Loading status for mutations */
  status?: 'error' | 'success' | 'pending';
}

/**
 * Inbound Webhook Section
 *
 * Displays inbound webhook configuration with header matching OutgoingWebhookSection
 * pattern, but single-item content area (not table) for 1:1 semantic relationship.
 *
 * Note: This is for INCOMING webhooks (Integration.hook) for data ingestion into
 * Chariot. Not to be confused with outgoing webhooks (Integration.webhook) that
 * send notifications to external systems.
 *
 * Structure:
 * - Header Row: Always visible with icon, title, description, and "Generate URL" button
 * - Content Row: Copy/Disconnect controls shown only when webhook exists (conditional)
 *
 * Used in:
 * - NotificationSettingsCards
 */
export const InboundWebhookSection: React.FC<InboundWebhookSectionProps> = ({
  hookAccount,
  hookUrl: _hookUrl,
  onGenerate,
  onDisconnect,
  onCopy,
  isCopied = false,
}) => {
  const { currentTheme } = useTheme();
  const hookMeta = AllIntegrations[Integration.hook];

  return (
    <section
      aria-labelledby="inbound-webhook-heading"
      data-testid="inbound-webhook-section"
    >
      <SettingContentCard padding="sm">
        <div className="flex flex-col gap-4">
          {/* Header Row - ALWAYS visible */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={`/icons/${currentTheme}/${hookMeta.logo}`}
                  alt={hookMeta.name}
                  className="size-5 shrink-0 opacity-50"
                />
                <div className="flex flex-col gap-1">
                  <h4 id="inbound-webhook-heading" className="font-bold">
                    Inbound Webhook
                  </h4>
                  <span className="text-chariot-text-secondary text-xs">
                    Use this URL to ingest objects into Chariot from external
                    sources
                  </span>
                </div>
              </div>
              {!hookAccount && onGenerate && (
                <Button
                  onClick={onGenerate}
                  size="sm"
                  data-testid="generate-inbound-webhook-button"
                ><PlusCircleIcon className="size-4" />Generate URL</Button>
              )}
            </div>
          </div>

          {/* Content Row - Only when webhook exists */}
          {hookAccount && (
            <div className="border-chariot-stroke rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={onCopy}
                  className={cn(
                    'rounded-md px-3 py-1.5 font-mono text-xs transition-colors',
                    isCopied
                      ? 'bg-green-500/10 text-green-600 ring-1 ring-green-500/20'
                      : 'bg-chariot-background-secondary text-chariot-text-primary hover:bg-chariot-background-tertiary cursor-pointer'
                  )}
                  aria-label="Copy webhook URL to clipboard"
                  data-testid="copy-inbound-webhook-button"
                  type="button"
                >
                  <DocumentDuplicateIcon
                    className="mr-1 inline size-3"
                    aria-hidden="true"
                  />
                  Copy URL
                </button>
                {onDisconnect && (
                  <Button
                    onClick={onDisconnect}
                    variant="ghost"
                    size="sm"
                    data-testid="disconnect-inbound-webhook-button"
                  >Disconnect</Button>
                )}
              </div>
            </div>
          )}
        </div>
      </SettingContentCard>
    </section>
  );
};
